import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../apiClient'

type HoursKey =
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'

type DailySkyviewStats = {
  engagementsCount: {
    current: number
    trend: number
  }
  withinTarget: {
    current: number
    trend: number
  }
  liveHighCount: number
  openCount: number
  inProgressCount: number
}

type PerProgramDailyOverview = {
  programId: string
  activeAutonomousSystemsCount: number
  withinTargetPercentage: number
  openCount: number
  inProgressCount: number
  rejectedCount: number
  timeoutCount: number
  tieBreakVoteCount: number
}

type DailySkyviewHourBreakdownPrediction = {
  hourOfDay: HoursKey
  type: 'prediction'
  predictedEngagementsCount: number
}

type DailySkyviewHourBreakdownHistory = {
  hourOfDay: HoursKey
  type: 'history'

  engagementsCount: number

  withinTargetCount: number
  missedTargetCount: number

  liveHighCount: number
  liveLowCount: number
  recyclerCount: number
  rejectedCount: number
  timeoutCount: number

  onlineMissionSpecialistsCount: number

  hadSurgeAlert: boolean
  hadCapacityAlert: boolean

  plannedHoursCount: number
  totalHoursCount: number

  avgMissionSpecialistTimeBusyPercentage: number
  avgTimeToTask: number

  tasksCreatedCount: number

  perProgram: {
    programId: string
    engagementsCount: number
    withinTargetCount: number | null
    activeAutonomousSystemsCount: number
    tasksCount: number
    liveHighCount: number
    liveLowCount: number
    recyclerCount: number
    rejectedCount: number
    timeoutCount: number
    tieBreakVoteCount: number
  }[]

  perMissionSpecialist: {
    appUserId: string
    name: string
    avatarUrl: string | null
    lastStatus: 'ONLINE' | 'IDLE' | 'OFFLINE'

    tasksCount: number
    avgTimeBusyPercentage: number

    avgTimeToClose: number

    isReplacingAnotherShift: boolean
    replacingUser: {
      appUserId: string
      name: string
      avatarUrl: string
    } | null
  }[]
}

type DailySkyviewHourBreakdown = DailySkyviewHourBreakdownPrediction | DailySkyviewHourBreakdownHistory

type MinimalProgramMeta = {
  id: string
  name: string
  displayName: string
  iconType: string
  color: string
  targetResolutionTime?: number | null
  version: number
}

type SkyviewDailyResponse = {
  stats: DailySkyviewStats
  perProgramDailyOverview: PerProgramDailyOverview[]
  breakdown: DailySkyviewHourBreakdown[]
  programs: MinimalProgramMeta[]
}

export function useSkyviewDaily(orgId: string | null, scope?: 'live') {
  return useQuery({
    queryKey: ['skyview-daily', orgId, scope],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const { data } = await apiClient.get<SkyviewDailyResponse>('/int/v3/skyview/daily', {
        params: { organizationId: orgId ?? undefined, scope },
      })
      return data
    },
  })
}
